// Styles
import styled from 'styled-components'

// Core
import {Divider} from '@material-ui/core'
import React, {useState, useEffect} from 'react'

// Components, actions, models, etc.
import Modal from 'components/Modal'
import {LoanDetailConfigType, LOAN_DETAILS_FIELD_CONFIG} from 'constants/loanDetailsConstants'
import type {RuleType} from 'services/formValidationHelpers'
import {validateForm} from 'services/formValidationHelpers'
import type {CreateOrderRequestBody, UploadedDocumentRequest} from 'types/orderTypes'
import type {PropertyType} from 'types/propertyTypes'
import CheckoutSummary from './CheckoutSummary'
import DeliveryDate from './DeliveryDate'
import DeliveryOptionFormFields from './DeliveryOptionFormFields'
import LoanDetailsFormField from './LoanDetailFormFields'
import PaymentFormField from './PaymentFormField'
import PropertyDetailsFormField from './PropertyDetailsFormField'
import UploadFormField from './UploadFormField'

// 3rd Party
import {openModal} from 'actions/modalActions'
import classnames from 'classnames'
import {MUST_AGREE_MESSAGE, UNSAVED_WORK_MODAL, USER_PROFILE_MODAL} from 'constants/modalsConstants'
import {userSelector} from 'selectors'
import {customerSelector} from 'selectors/customerSelectors'
import type {CDAPriceType} from 'hooks/useProductPrice'
import {Form} from 'react-final-form'
import {useDispatch, useSelector} from 'react-redux'
import {getTotalPriceWithTax} from 'services/cdaTotalPriceServices'

type CDACheckoutModalProps = {
  className?: string
  handleClose: () => void
  handleExited: () => void
  open: boolean
  property: PropertyType
  placeOrder: (order: CreateOrderRequestBody, price: string) => void
}

type FormValues = {
  [key in LoanDetailConfigType['id'] | 'document' | 'accepted' | 'pricing']: key extends 'document'
    ? UploadedDocumentRequest
    : key extends 'accepted'
    ? boolean
    : key extends 'pricing'
    ? CDAPriceType
    : string
}

const DESCRIPTION = 'Almost there. We just need a few more details from you'
const CDACheckoutModal = ({
  className,
  handleClose,
  handleExited,
  open,
  property,
  placeOrder
}: CDACheckoutModalProps) => {
  const dispatch = useDispatch()
  const [submitting, setSubmitting] = useState(false)
  const userData = useSelector(userSelector)
  const customer = useSelector(customerSelector)
  const {firstName, lastName, email, phone} = userData

  const isRequiredInfoReady = !!(
    firstName &&
    lastName &&
    email &&
    customer?.name &&
    customer.billingContact?.email &&
    customer.billingContact?.lastName &&
    customer.billingContact?.firstName
  )

  useEffect(() => {
    if (!isRequiredInfoReady) {
      dispatch(
        openModal({
          modalType: USER_PROFILE_MODAL,
          title: 'Complete your profile to continue',
          subTitle: 'To place your order, please confirm the following details',
          onConfirmText: 'Continue ->',
          mixpanelEventData: {
            initiatedFrom: 'CDA checkout Modal'
          }
        })
      )
    }
  }, [dispatch, isRequiredInfoReady])

  const onSubmit = async ({document, accepted, pricing, ...values}: FormValues) => {
    setSubmitting(true)

    const order = {
      ...values,
      productType: 'CDA' as const,
      ...(pricing.isMLSEnabled && {productOptionsList: [{key: 'MLS_SHEETS', value: 'true'}]}),
      dueDate: pricing.dueDate,
      customerDocumentList: [document],
      ...(firstName &&
        lastName &&
        email && {
          requestor: {
            firstName,
            lastName,
            email,
            ...(phone && {phone})
          }
        })
    }

    await placeOrder(order, getTotalPriceWithTax(pricing.price))
    setSubmitting(false)
  }

  const validate = (values: FormValues) => {
    const fieldRulesDictionary = LOAN_DETAILS_FIELD_CONFIG.reduce((ruleMap, {id, validation}) => {
      if (validation) {
        ruleMap[id] = validation
      }
      return ruleMap
    }, {} as {[key: string]: RuleType[]})

    const error = validateForm(fieldRulesDictionary, values)
    if (!values.document) {
      error.document = true
    }

    if (!values.accepted) {
      error.accepted = MUST_AGREE_MESSAGE
    }

    return error
  }

  return (
    <Form
      onSubmit={onSubmit}
      validate={validate}
      render={({handleSubmit, dirty, values}) => (
        <CDACheckoutModal.Styled
          className={classnames('cda-checkout-modal', className)}
          open={open && isRequiredInfoReady}
          disableEnforceFocus
          showCloseButton
          size='lg'
          closeOnBackdropClick={false}
          onClose={() => {
            if (dirty) {
              dispatch(
                openModal({
                  modalType: UNSAVED_WORK_MODAL,
                  onConfirm: closeModal => () => {
                    closeModal()
                    handleClose()
                  }
                })
              )
            } else {
              handleClose()
            }
          }}
          onExited={handleExited}
        >
          <Modal.Header>Checkout</Modal.Header>
          <Divider />
          <form onSubmit={handleSubmit}>
            <Modal.Content>
              <div className='left-content'>
                <p className='left-content-description'>{DESCRIPTION}</p>
                <PropertyDetailsFormField property={property} />
                <UploadFormField field='document' label='Original Appraisal' />
                <LoanDetailsFormField />
                <DeliveryOptionFormFields address={property.address || {}} field='pricing' />
                <PaymentFormField />
              </div>
              <Divider orientation='vertical' />
              <div className='right-content'>
                <DeliveryDate dueDate={values.pricing?.dueDate} />
                <CheckoutSummary
                  turnTime={values.pricing?.turnTime}
                  isMLSEnabled={values.pricing?.isMLSEnabled}
                  price={values.pricing?.price}
                  handleFormSubmit={handleSubmit}
                  submitting={submitting}
                  agreementField='accepted'
                />
              </div>
            </Modal.Content>
          </form>
        </CDACheckoutModal.Styled>
      )}
    />
  )
}

CDACheckoutModal.Styled = styled(Modal)`
  .header {
    padding: 20px 24px;
  }

  .content.portal-MuiDialogContent-root {
    padding: 0;
  }

  .content {
    // modal marginTop + marginBottom = 64px
    // modal header height = 72px
    // content paddingBottom = 24px
    height: calc(100vh - 64px - 72px - 24px);
    display: flex;

    .left-content,
    .right-content {
      overflow: auto;
    }

    .left-content {
      flex: 1;
      padding: 24px;
      row-gap: 20px;
      display: flex;
      flex-direction: column;

      &-description {
        font-size: 1rem;
        margin: 0;
      }
    }

    .right-content {
      padding: 24px;
    }
  }
`

export default CDACheckoutModal
